import React from 'react'
import { Field, Form } from 'react-final-form'
import { Button, Col, FormGroup, FormText, InputGroup, InputGroupAddon, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, UncontrolledTooltip } from 'reactstrap'

import FA from '@src/components/common/FontAwesomeIcon'
import { IOption } from '@src/components/common/Select'
import ValidatedInput from '@src/components/common/ValidatedInput'
import ValidatedSelect from '@src/components/common/ValidatedSelect'
import { FieldPrefix, PrefixedField } from '@src/components/forms/FieldPrefix'
import { IModalProps } from '@src/hooks/useModal'
import { required } from '@src/logic/forms/validation'
import { CommitmentDefinitionCreate } from '@src/logic/http/Api'
import { CommitmentDefinition } from '@src/types/costs'

export interface INewCommitmentDefinitionFormData {
    code: string
    name: string
    commitmentNumberDefinition: {
        prefix: string
        significantPlaces: number
        currentNumber: number
    }
    defaultVariationAdditionsPercentage: number
    defaultVariationDeductionsPercentage: number
    defaultDocumentLinkCategories: IOption<string>[]
}

function normalizeCode(value?: string) {
    return value?.toUpperCase()
}

function defaultDocumentLinkMessage() {
    return 'Start typing to create a new category...'
}

interface IProps extends IModalProps {
    projectId: string
    onCreated?: (definition: CommitmentDefinition) => void
}

const initalValues: Partial<INewCommitmentDefinitionFormData> = {
    commitmentNumberDefinition: {
        prefix: '',
            significantPlaces: 2,
                currentNumber: 0
    },
    defaultVariationAdditionsPercentage: 0,
        defaultVariationDeductionsPercentage: 0,
            defaultDocumentLinkCategories: []
}

export default function NewCommitmentDefinitionModal({ projectId, onCreated, ...modalProps }: IProps) {
    async function createCommitmentType(values: INewCommitmentDefinitionFormData) {
        const newDefinitionResponse = await CommitmentDefinitionCreate(projectId, {
            code: values.code,
            name: values.name,
            commitmentNumberDefinition: values.commitmentNumberDefinition,
            statusDefinitions: {},
            defaultVariationAdditions: values.defaultVariationAdditionsPercentage / 100,
            defaultVariationDeductions: values.defaultVariationDeductionsPercentage / 100,
            defaultDocumentLinkCategories: values.defaultDocumentLinkCategories?.map(x => x.value) ?? [],
            defaultPaymentDocumentLinkCategories: []
        })
        onCreated?.(newDefinitionResponse.data)
        modalProps.toggle()
    }

    return (
        <Modal {...modalProps}>
            <Form initialValues={initalValues} onSubmit={createCommitmentType}>
                {({ handleSubmit }) =>
                    <>
                        <ModalHeader toggle={modalProps.toggle}>New Commitment Type</ModalHeader>
                        <ModalBody>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label>Code</Label>
                                        <Field name="code" component={ValidatedInput} validate={required} parse={normalizeCode} />
                                        <FormText>A short coded identifier for the commitment type</FormText>
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label>Name</Label>
                                        <Field name="name" component={ValidatedInput} validate={required} />
                                        <FormText>A common name for the type</FormText>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <h5 className="border-bottom">Auto-number settings <FA id="form-commitmentdefinition-autonumber" icon="question-circle" /></h5>
                            <UncontrolledTooltip target="form-commitmentdefinition-autonumber">These settings affect the auto-generated number when this commitment is created</UncontrolledTooltip>
                            <Row>
                                <FieldPrefix prefix="commitmentNumberDefinition">
                                    <Col>
                                        <FormGroup>
                                            <Label>Prefix</Label>
                                            <PrefixedField bsSize="sm" name="prefix" component={ValidatedInput} validate={required} parse={normalizeCode} />
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup>
                                            <Label>Significant Places</Label>
                                            <PrefixedField bsSize="sm" name="significantPlaces" component={ValidatedInput} validate={required} type="number" min={2} max={10} />
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup>
                                            <Label>Start Index</Label>
                                            <PrefixedField bsSize="sm" name="currentNumber" component={ValidatedInput} validate={required} />
                                        </FormGroup>
                                    </Col>
                                </FieldPrefix>
                            </Row>
                            <h5 className="border-bottom">Default settings</h5>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label>Additions</Label>
                                        <InputGroup size="sm">
                                            <Field name="defaultVariationAdditionsPercentage" component={ValidatedInput} type="number" />
                                            <InputGroupAddon addonType="append">%</InputGroupAddon>
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label>Deductions</Label>
                                        <InputGroup size="sm">
                                            <Field name="defaultVariationDeductionsPercentage" component={ValidatedInput} type="number" />
                                            <InputGroupAddon addonType="append">%</InputGroupAddon>
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label>Document Link Categories</Label>
                                        <Field name="defaultDocumentLinkCategories" component={ValidatedSelect} isMulti selectType="creatable" placeholder="Create..." noOptionsMessage={defaultDocumentLinkMessage} />
                                        <FormText>Categories of documents that can be attached to the commitment</FormText>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={handleSubmit}>Create</Button>
                        </ModalFooter>
                    </>
                }
            </Form>
        </Modal>
    )
}
